import * as React from 'react';
import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useQuery, useQueryClient } from 'react-query';
import Avatar from '@mui/material/Avatar';
import apiClient from '../../request/http-common';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Button, TextField, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Slide from '@mui/material/Slide';
import { useMutation } from 'react-query';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function AdminWalletOptions() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    image_url: '',
    order: 0,
    is_active: true,
  });
  const [imageFile, setImageFile] = useState(null);

  const { data, isLoading, refetch } = useQuery(
    'admin-wallet-options',
    async () => await apiClient.get('/api/admin/wallet-options'),
    { enabled: true }
  );
  const options = data?.data?.wallet_options || [];

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({ name: '', image_url: '', order: 0, is_active: true });
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (opt) => {
    setEditingId(opt.id);
    setForm({
      name: opt.name,
      image_url: opt.image_url || '',
      order: opt.order || 0,
      is_active: opt.is_active !== false,
    });
    setImageFile(null);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm({ name: '', image_url: '', order: 0, is_active: true });
    setImageFile(null);
  };

  const saveMutation = useMutation(
    async () => {
      if (imageFile) {
        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('order', String(form.order));
        formData.append('is_active', form.is_active ? '1' : '0');
        formData.append('image', imageFile);
        const url = editingId
          ? `/api/admin/wallet-options/${editingId}`
          : '/api/admin/wallet-options';
        return await apiClient.post(url, formData, {
          transformRequest: [(data, headers) => {
            if (data instanceof FormData) {
              delete headers['Content-Type'];
              return data;
            }
            return data;
          }],
        });
      }
      const payload = {
        name: form.name,
        order: form.order,
        is_active: form.is_active,
        image_url: form.image_url || undefined,
      };
      if (editingId) {
        return await apiClient.post(`/api/admin/wallet-options/${editingId}`, payload);
      }
      return await apiClient.post('/api/admin/wallet-options', payload);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({ message: res.data.message, type: 'success' }));
          window.dispatchEvent(new Event('newMessage'));
          queryClient.invalidateQueries('admin-wallet-options');
          refetch();
          handleClose();
        }
      },
      onError: (err) => {
        const msg = err.response?.data?.message || err.message || 'Request failed';
        localStorage.setItem('notification', JSON.stringify({ message: msg, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      },
    }
  );

  const deleteMutation = useMutation(
    async (id) => await apiClient.delete(`/api/admin/wallet-options/${id}`),
    {
      onSuccess: () => {
        localStorage.setItem('notification', JSON.stringify({ message: 'Wallet option deleted.', type: 'success' }));
        window.dispatchEvent(new Event('newMessage'));
        queryClient.invalidateQueries('admin-wallet-options');
        refetch();
        setDeleteDialogOpen(false);
        setDeleteId(null);
      },
      onError: (err) => {
        const msg = err.response?.data?.message || err.message || 'Delete failed';
        localStorage.setItem('notification', JSON.stringify({ message: msg, type: 'error' }));
        window.dispatchEvent(new Event('newMessage'));
      },
    }
  );

  return (
    <div style={{ marginTop: '75px' }}>
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: 20, textTransform: 'uppercase' }} variant="h6">
            Wallet options (Connect Wallet)
          </Typography>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add wallet option
          </Button>
        </Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="right">Order</TableCell>
                <TableCell align="right">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options.map((opt) => (
                <TableRow key={opt.id}>
                  <TableCell>
                    {opt.image_url ? (
                      <Avatar src={opt.image_url} sx={{ width: 40, height: 40 }} variant="rounded" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{opt.name}</TableCell>
                  <TableCell align="right">{opt.order}</TableCell>
                  <TableCell align="right">{opt.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => handleOpenEdit(opt)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => { setDeleteId(opt.id); setDeleteDialogOpen(true); }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={dialogOpen} onClose={handleClose} TransitionComponent={Transition} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit wallet option' : 'Add wallet option'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. MetaMask, Trust Wallet"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL (full URL e.g. https://...)"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label">
                Or upload image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => { setImageFile(e.target.files?.[0] || null); setForm((f) => ({ ...f, image_url: '' })); }}
                />
              </Button>
              {imageFile && <Typography variant="caption" sx={{ ml: 1 }}>{imageFile.name}</Typography>}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={() => saveMutation.mutate()} disabled={!form.name.trim() || saveMutation.isLoading}>
            {saveMutation.isLoading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete wallet option?</DialogTitle>
        <DialogContent><Typography>This will remove the option from the Connect Wallet list. Existing submitted addresses are not deleted.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => deleteId && deleteMutation.mutate(deleteId)} disabled={deleteMutation.isLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
